import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────── Types ────────────────────────── */
type SubscriptionTier = "free" | "pro" | "enterprise" | null;

interface AuthState {
  idToken: string | null;
  userName: string | null;
  tier: SubscriptionTier;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  refreshAuth: () => Promise<void>;
  logout: () => void;
}

/* ────────────────────────── Constants ──────────────────────── */
const TOKEN_REFRESH_THRESHOLD_SECONDS = 600;
// Construct a base URL for API requests. If REACT_APP_API_URL is provided, it
// should be the root domain without a trailing slash. The "/api" prefix is
// appended automatically. When the variable is undefined (e.g. local dev),
// default to just using the "/api" path.
const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

/* ────────────────────── Utility functions ───────────────────── */
function decodeJwtPayload(token: string) {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    ) as { exp?: number };
  } catch {
    return null;
  }
}

async function refreshTokens(refreshToken: string): Promise<{ token?: string } | null> {
  const res = await fetch(`${API_BASE}/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: refreshToken }),
  });
  return res.ok ? res.json() : null;
}

/* ───────────────────────── Context ─────────────────────────── */
const AuthContext = createContext<AuthContextType>({
  idToken: null,
  userName: null,
  tier: null,
  loading: true,
  refreshAuth: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

/* ─────────────────────── Provider ──────────────────────────── */

// Define public paths that do not require authentication
const publicPaths = ["/", "/login", "/signup", "/about", "/verify"];

interface Props {
  children: ReactNode;
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();

  const [state, setState] = useState<AuthState>({
    idToken: null,
    userName: null,
    tier: null,
    loading: true,
  });

  const logout = useCallback(() => {
    sessionStorage.clear();
    setState({
      idToken: null,
      userName: null,
      tier: null,
      loading: false,
    });
    navigate("/"); // Soft redirect — no flicker
  }, [navigate]);

  const loadAuth = useCallback(async () => {
    let idToken = sessionStorage.getItem("id_token");
    const refreshToken = sessionStorage.getItem("refresh_token");
    console.log("[AuthContext] Tokens:", { idToken, refreshToken });

    if (!idToken || !refreshToken) {
      console.warn("[AuthContext] Missing tokens, clearing auth state");
      setState({
        idToken: null,
        userName: null,
        tier: null,
        loading: false,
      });
      if (!publicPaths.some((p) => window.location.pathname.startsWith(p))) {
        navigate("/");
      }
      return;
    }

    const payload = decodeJwtPayload(idToken);
    if (!payload?.exp) {
      console.warn("[AuthContext] Invalid JWT payload, logging out");
      logout();
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp - now < TOKEN_REFRESH_THRESHOLD_SECONDS) {
      console.log("[AuthContext] Token expiring soon, refreshing");
      const fresh = await refreshTokens(refreshToken);
      if (fresh?.token) {
        console.log("[AuthContext] Refreshed tokens");
        idToken = fresh.token;
        sessionStorage.setItem("id_token", idToken);
      } else {
        console.warn("[AuthContext] Failed to refresh tokens, logging out");
        logout();
        return;
      }
    }

    try {
      const res = await fetch(`${API_BASE}/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
        cache: "no-store",
      });
      console.log("[AuthContext] /api/me response status:", res.status);

      if (!res.ok) throw new Error(`backend ${res.status}`);

      const me: { userName: string; tier: SubscriptionTier } = await res.json();
      console.log("[AuthContext] /api/me user:", me);
      setState({
        idToken,
        userName: me.userName,
        tier: me.tier,
        loading: false,
      });
    } catch (e) {
      console.error("[AuthContext] Failed to fetch /api/me or set state", e);
      logout();
    }
  }, [logout, navigate]);

  useEffect(() => {
    loadAuth();
    const iv = setInterval(loadAuth, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [loadAuth]);

  return (
    <AuthContext.Provider value={{ ...state, refreshAuth: loadAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
